import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CounterPartyGridModel } from '../../../core/models/CounterPartyGridModel';
import { Store } from '../../../store';
import { DataService } from '../../../core/services/data.service';
import { CellRenderersService } from '../../../core/services/cellRenderers.service';
import { UtilityService } from '../../../core/services/utility.service';
import { InstrumentGridModel } from '../../../core/models/InstrumentGridModel';
import { GridOptions, RowClassRules } from 'ag-grid-community';
import { QuotaModalComponent } from '../../../layout/components/quota-modal/quota-modal.component';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { SmallCcrChartComponent } from '../../shared/components/small-ccr-chart/small-ccr-chart.component';
import { AampQuota } from '../../../core/models/AampQuota';
import { User } from '../../../core/models/User';
import { QueryConfig } from '../../../core/models/QueryConfig';
import * as _ from 'underscore';
import { ConstantsService } from '../../../core/services/constants.service';
import { Observable, Subject, combineLatest, of } from 'rxjs';
import { CsvExportParams } from 'ag-grid-community';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ApplicationEventsService } from '../../../core/services/application-events.service';
import { isNil } from 'lodash-es';
import { catchError, tap, mergeMap, takeUntil, distinctUntilChanged, filter } from 'rxjs/operators';
import { UpdateProjectionsRequest } from '../../../core/models/updateprojectionsRequest';
import { InstrumentItem } from '../../../core/models/InstrumentItem';


@Component({
    selector: 'app-entity-summary',
    templateUrl: './entity-summary.component.html',
    styleUrls: ['./entity-summary.component.scss'],
    standalone: false
})
export class EntitySummaryComponent implements OnDestroy, OnInit {
    public unsubscribe: Subject<void> = new Subject();

    entityCcrExeChartData: any[];
    entityCcrExeChartDataSimu: any[];
    user: User;
    currentQueryConfig = <QueryConfig>{
        rolloverToCash: true,
        setForGroup: false,
        entityTailConfigs: undefined,
        groupTailConfig: undefined,
        modelId: null
    };
    counterPartyGridData: CounterPartyGridModel[];
    counterPartyGridColumnDef: any[];

    instrumentGridData: InstrumentGridModel[];
    instrumentGridDataSimu: InstrumentGridModel[];
    instrumentGridColumnDef: any[];

    overlayLoadingTemplate: string;
    overlayNoRowsTemplate: string;

    isModellingOnPage = false;
    isAnyModelSelectedForModelling = false;
    instrumentGridCollapse = false;
    components: any;
    hasCounterPartyGridInputChanged = false;
    domLayout: 'normal' | 'autoHeight' | 'print' = 'autoHeight';
    instrumentGridOptions = <GridOptions>{};
    form: UntypedFormGroup;

    updateProjectionsLabel = 'UPDATE PROJECTIONS';
    projectionsAreBeingUpdated = false;

    public counterPartyGridApi;
    public instrumentGridApi;
    public entityName: string;
    public entityId: string;

    rowClassRules:RowClassRules  = {
        'last-child-column': (params) => {
            return params.data.isLastChildColumn? true: false;
          }
    };

    instrumentGridToolBarOption = {
        horizon: {
            text: 'Now to 1 years',
            from: 0,
            to: 12
        },
        instrumentType: '-1',
        groupBy: 'derivative',
        searchText: ''
    };

    /* AAMP percentages. */
    usedQuotaData: any[];
    usedQuotaDataSimu: any[];
    derivativeHoldings: any[];
    ammpItems: any[];
    aampQuotas: AampQuota[];

    /* Used for AAMP tooltips. */
    aampProductInfoList: any[];

    /* Used for initialization of fields with USD currency */
    fxRates: any[];
    modelLeg1NotionalInUsd: any;
    showUpdateProjectionsButton: boolean = false;

    highlightedSimu = {};

    selectedCcrExe = 'CCR';
    selectedCcrExeChartMode = '';

    gridOptions = <GridOptions>{
        suppressCellSelection: true,
        rowHeight: 30
    };

    showAampProductTooltip = false;
    selectedTooltipProduct = null;
    tooltipClickFilter = false;
    Math = Math;

    get counterPartyList() {
        if (!this.form) {
            return null;
        }
        return this.form.get('counterParties') as UntypedFormArray;
    }

    ngOnInit() {
        combineLatest(
            this.route.params,
            this.store.select<User>('user').pipe(distinctUntilChanged((prev, curr) => _.isEqual(prev, curr)))
        ).pipe(takeUntil(this.unsubscribe))
            .subscribe(([params, user]) => {
                this.entityId = params.entityId;
                this.entityName = this.getLegalEntityName(this.entityId);
                this.user = user;
                this.isModellingOnPage = (this.store.value.urlModelToggle[this.router.url] &&
                    this.store.value.urlModelToggle[this.router.url] === true);
                this.isAnyModelSelectedForModelling = (this.store.value.modellingConfig.modelId != null);
                this.resetPageData();
                this.reloadPage();
            });

        this.components = { simpleCellRenderer: this.getSimpleCellRenderer() };
        this.subscribeOnPageReload();
    }

    constructor(private dataService: DataService, private route: ActivatedRoute, private router: Router, private store: Store,
        private cellRenderersService: CellRenderersService, private utilityService: UtilityService,
        private modalService: NgbModal, private constantsService: ConstantsService, private fb: UntypedFormBuilder,
        private appEvents: ApplicationEventsService) {
        this.overlayLoadingTemplate = this.utilityService.overlayLoadingTemplate;
        this.overlayNoRowsTemplate = this.utilityService.overlayNoRowsTemplate;
        
        // Initialize form to avoid null reference errors
        this.form = this.fb.group({
            counterParties: this.fb.array([])
        });

        this.counterPartyGridColumnDef = [
            {
                headerName: 'CPY',
                headerClass: 'cpy',
                children: [
                    { headerName: '', field: 'counterParty', width: 110 }
                ]
            }, {
                headerName: 'SHARE',
                children: [
                    { headerName: '%', field: 'notionalSharePercentage', width: 220, editable: true }
                ]
            }, {
                headerName: 'NOTIONAL',
                children: [
                    { headerName: 'M USD', field: 'notionalInUsd', width: 220, editable: true }
                ]
            }, {
                headerName: 'CCR PROJECTION',
                children: [
                    { headerName: '(10 YEAR)', field: 'lineChartData', width: 220, cellRendererFramework: SmallCcrChartComponent }
                ]
            }, {
                headerName: 'LAST MONTH',
                children: [
                    { headerName: 'WCCR %', field: 'lastMonthWccr', width: 130 },
                    { headerName: '2w VaR M USD', field: 'lastMonthW2VarMUsd', width: 130 },
                    { headerName: 'PLE M USD', field: 'lastMonthPleMUsd', width: 130 },
                ]
            }, {
                headerName: '0-2 YEARS',
                children: [
                    { headerName: 'WCCR %', field: 'zeroToTwoYearsWccr', width: 130 },
                    { headerName: '2w VaR M USD', field: 'zeroToTwoYearsW2VarMUsd', width: 130 },
                    { headerName: 'PLE M USD', field: 'zeroToTwoYearsPleMUsd', width: 130 },
                    { headerName: 'DATE', field: 'zeroToTwoYearsDate', width: 130 }
                ]
            }, {
                headerName: '2-5 YEARS',
                children: [
                    { headerName: 'WCCR %', field: 'twoToFiveYearsWccr', width: 130 },
                    { headerName: 'PLE M USD', field: 'twoToFiveYearsPleMUsd', width: 130 },
                    { headerName: 'DATE', field: 'twoToFiveYearsDate', width: 130 }
                ]
            }, {
                headerName: '5-10 YEARS',
                children: [
                    { headerName: 'WCCR %', field: 'fiveToTenYearsWccr', width: 130 },
                    { headerName: 'PLE M USD', field: 'fiveToTenYearsPleMUsd', width: 130 },
                    { headerName: 'DATE', field: 'fiveToTenYearsDate', width: 130 }
                ]
            }
        ];

        this.instrumentGridColumnDef = [
            {
                headerName: 'Type',
                field: 'type',
            }, {
                headerName: 'Product',
                field: 'product'
            }, {
                headerName: 'Counter Party',
                field: 'counterParty',
                width: 200
            }, {
                headerName: 'ID',
                field: 'instrumentId'
            }, {
                headerName: 'CCY',
                field: 'currency'
            }, {
                headerName: 'Notional',
                field: 'notional'
            }, {
                headerName: 'Value today',
                field: 'valueToday'
            }, {
                headerName: 'Min Val',
                field: 'minVal'
            }, {
                headerName: 'Min Val Date',
                field: 'minValDate'
            }, {
                headerName: 'Max Val',
                field: 'maxVal'
            }, {
                headerName: 'Max Val Date',
                field: 'maxValDate'
            }, {
                headerName: 'MV (Tail %)',
                field: 'valDiff'
            }, {
                headerName: 'Date MV',
                field: 'valDiffDate'
            }, {
                headerName: 'Expiry',
                field: 'expiry'
            }
        ];
    }

    onCounterPartyGridReady(params) {
        this.counterPartyGridApi = params.api;
        //this.counterPartyGridApi.setGridAutoHeight(true);
        this.counterPartyGridApi.startEditingCell({
            rowIndex: 0,
            colKey: 'notionalSharePercentage'
        });
        this.counterPartyGridApi.startEditingCell({
            rowIndex: 0,
            colKey: 'notionalInUsd'
        });
        params.api.sizeColumnsToFit();
    }

    onInstrumentGridReady(params) {
        this.instrumentGridApi = params.api;
        params.api.sizeColumnsToFit();
        this.instrumentGridOptions.getRowClass = ((params) => {
            if (params && params.node && params.node.data['simu']) {
                return 'bp2s-simu-fg';
            }

        })
    }

    getNodeChildDetails(rowItem): any {
        if (rowItem.childInstruments) {
            return {
                group: true,
                expanded: false,
                children: rowItem.childInstruments,
                key: rowItem.type
            };
        } else {
            return null;
        }
    }

    getSimpleCellRenderer() {

        function SimpleCellRenderer() {
        }

        SimpleCellRenderer.prototype.init = function (params) {
            const tempDiv = document.createElement('div');
            if (params.node.group) {
                tempDiv.innerHTML =
                    '<span>' + params.value + '</span>';
                   
            } else {
                tempDiv.innerHTML = '<span><span><i class="material-icons md-15">subdirectory_arrow_right</i></span> <span>'
                    + params.value + '</span></span>';
                    
            }
            this.eGui = tempDiv.firstChild;
            
        };
        SimpleCellRenderer.prototype.getGui = function () {
            return this.eGui;
        };
       
        return SimpleCellRenderer;
    }

    onUpdateHorizonTimeRange(horizon: any) {
        this.instrumentGridToolBarOption.horizon.from = horizon.from;
        this.instrumentGridToolBarOption.horizon.to = horizon.to;
        this.getInstruments();
    }

    selectedGridInstrumentTypeChange(instrumentType) {
        this.instrumentGridToolBarOption.instrumentType = instrumentType;
        this.getInstruments();
    }

    onInstrumentPanelClick() {
        this.instrumentGridCollapse = !this.instrumentGridCollapse;
        if (this.instrumentGridCollapse) {
            this.getInstruments();
        }
    }

    onInstrumentGridCollapseAll() {
        this.instrumentGridOptions.api.forEachNode(node => {
            if (node.group) {
                node.expanded = false;
            }
        });
        this.instrumentGridOptions.api.onGroupExpandedOrCollapsed();
    }

    onInstrumentGridExpandAll() {
        this.instrumentGridOptions.api.forEachNode(node => {
            if (node.group) {
                node.expanded = true;
            }
        });
        this.instrumentGridOptions.api.onGroupExpandedOrCollapsed();
    }

    openQuotaModal(userNotification: boolean) {
        const modalRef = this.modalService.open(QuotaModalComponent,
            { centered: true, windowClass: 'quota-modal' });
        modalRef.result.then((result) => {
            this.dataService.updateAampMaxQuota(this.entityName, result)
                .pipe(takeUntil(this.unsubscribe))
                .subscribe(x => {
                    if (x) {
                        this.dataService.clearCacheData();
                        this.aampQuotas = result;
                        this.reloadPageData();
                        return;
                    }
                    alert('Error happened, while trying to update "Aamp Quota Data"...');
                }, error => {
                    alert('Error happened, while trying to update "Aamp Quota Data"...');
                });
        },
            (reason) => {
            }).catch(() => {
            });

        modalRef.componentInstance.aampQuotas = this.aampQuotas;
        modalRef.componentInstance.entityName = this.entityName;
        modalRef.componentInstance.userNotification = userNotification;
    }

    onAampTableBlur() {
        this.showAampProductTooltip = false;
    }

    onAampProductClick(productName, event) {
        let foundObj = _.find(this.aampProductInfoList, (item: any) => item.productName === productName);
        if (!foundObj) {
            return;
        }
        this.selectedTooltipProduct = foundObj;
        this.showAampProductTooltip = true;

        const x = event.clientX,
            y = event.clientY;
        const tooltipElement = document.getElementById('aamp-tooltip');
        tooltipElement.style.top = (y - 105) + 'px';
        tooltipElement.style.left = (x - 10) + 'px';
    }

    onAampTooltipLinkClick() {
        if (!this.selectedTooltipProduct) {
            return;
        }

        this.tooltipClickFilter = true;
        this.instrumentGridCollapse = true;
        this.getInstruments(this.tooltipClickFilter);
        setTimeout(() => {
            document.getElementById('aamp-instrument-filter').scrollIntoView();
        }, 0);
    }

    removeAampTooltipClickFilter() {
        this.tooltipClickFilter = false;
        this.getInstruments(this.tooltipClickFilter);
    }

    public subscribeIsModellingOnPageChange() {
        this.store.select<string>('urlModelToggle')
            .pipe(
                filter(Boolean),
                takeUntil(this.unsubscribe)
            )
            .subscribe(x => {
                const modelMode = x[this.router.url];
                if (modelMode !== undefined && modelMode !== null && _.isBoolean(modelMode) && modelMode !== this.isModellingOnPage) {
                    this.isModellingOnPage = modelMode;
                    this.reloadPage();
                }
            });
    }

    public resetPageData() {
        this.usedQuotaData = null;
        this.usedQuotaDataSimu = null;
        this.entityCcrExeChartData = [];
        this.entityCcrExeChartDataSimu = [];

        this.aampProductInfoList = [];
        this.instrumentGridCollapse = false;

        this.instrumentGridDataSimu = [];
        this.highlightedSimu = {};
    }

    public reloadPage() {
        if (!this.isModellingOnPage) {
            this.setNonModelMode();
            this.loadPageData();
            return;
        } else {
            this.setModelMode();
            this.loadModelPageData();
        }
    }

    public setModelMode() {
        this.currentQueryConfig.rolloverToCash = this.store.value.modellingConfig.rolloverToCash;
        this.currentQueryConfig.setForGroup = this.store.value.modellingConfig.setForGroup;
        this.currentQueryConfig.groupTailConfig = this.store.value.modellingConfig.groupTailConfig;
        this.currentQueryConfig.entityTailConfigs = this.store.value.modellingConfig.entityTailConfigs;
        this.currentQueryConfig.modelId = this.store.value.modellingConfig.modelId;
    }

    public setNonModelMode() {
        this.currentQueryConfig.rolloverToCash = this.user.queryConfig.rolloverToCash;
        this.currentQueryConfig.setForGroup = this.user.queryConfig.setForGroup;
        this.currentQueryConfig.groupTailConfig = this.user.queryConfig.groupTailConfig;
        this.currentQueryConfig.entityTailConfigs = this.user.queryConfig.entityTailConfigs;
        this.currentQueryConfig.modelId = null;
    }

    public loadPageData() {
        this.getFxRates();
        this.getAampProductInfo();
        this.getQuotaData();
        this.getCcrAndEceData();
    }
    public loadModelPageData() {
        this.dataService.clearCacheData();
        this.getFxRates();
        this.getAampProductInfoSimu();
        this.getQuotaDataSimu();
        this.getCcrAndEceDataSimu(null);
    }

    public getFxRates() {
        this.dataService.getFxRates()
            .pipe(takeUntil(this.unsubscribe))
            .subscribe(result => {
                this.fxRates = result.fxRates;
            }, error => {
                this.fxRates = [];
                alert('Error happened, while trying to load "FX Rates"...');
            })
    }

    public getAampProductInfo() {
        this.dataService.getAampProductInfo(this.entityName, 0, 0, this.instrumentGridToolBarOption.instrumentType,
            this.currentQueryConfig.setForGroup ? this.currentQueryConfig.groupTailConfig : null,
            this.currentQueryConfig.entityTailConfigs, null, this.currentQueryConfig.rolloverToCash)
            .pipe(takeUntil(this.unsubscribe))
            .subscribe(x => {
                this.aampProductInfoList = x.aampProductInfoList;
            }, error => {
                this.aampProductInfoList = [];
                alert('Error happened, while trying to load "Aamp Product Information Data"...');
            });
    }
    public getAampProductInfoSimu() {
        const model = this.currentQueryConfig.modelId;
        const aampProductInfoSimu$ = this.dataService.getAampProductInfoSimu(
            this.entityName, model, 0, 0, this.instrumentGridToolBarOption.instrumentType,
            this.currentQueryConfig.setForGroup ? this.currentQueryConfig.groupTailConfig : null,
            this.currentQueryConfig.entityTailConfigs, null, this.currentQueryConfig.rolloverToCash);
        aampProductInfoSimu$.pipe(takeUntil(this.unsubscribe))
            .subscribe(x => {
                this.aampProductInfoList = x.aampProductInfoSimuList;
            }, error => {
                this.aampProductInfoList = [];
                alert('Error happened, while trying to load "Aamp Product Information Data" (+simu) ...');
            });
    }

    public getQuotaData() {
        this.dataService.getAampQuotaData(this.entityName)
            .pipe(takeUntil(this.unsubscribe))
            .subscribe(x => {
                this.aampQuotas = x.aampQuotas;
                this.getAampUsedQuotaData();
            }, error => {
                this.aampQuotas = [];
                alert('Error happened, while trying to load "Aamp Quota Data"...');
            });
    }

    public getAampUsedQuotaData() {
        this.dataService.getAampUsedQuotaData(this.entityName, 0, 0, this.instrumentGridToolBarOption.instrumentType,
            this.currentQueryConfig.setForGroup ? this.currentQueryConfig.groupTailConfig : null,
            this.currentQueryConfig.entityTailConfigs, null, this.currentQueryConfig.rolloverToCash)
            .pipe(takeUntil(this.unsubscribe))
            .subscribe((x) => {
                x.derivativeHoldings.sort((a, b) => a.productName.localeCompare(b.productName));
                this.usedQuotaData = x.derivativeHoldings;
                this.derivativeHoldings = x.derivativeHoldings;
                if (this.store.value.user.isAdmin && _.any(this.usedQuotaData, (item: any) => item.maxQuota === 0)) {
                    this.addNewInstrumentProductsToQuotaArray(this.usedQuotaData);
                    this.openQuotaModal(true);
                }
            });
    }

    public getQuotaDataSimu() {
        const derivativeHoldings$ = this.dataService.getAampQuotaData(this.entityName)
            .pipe(takeUntil(this.unsubscribe))
            .pipe(
                tap(x => this.aampQuotas = x.aampQuotas),
                mergeMap(x => this.dataService.getAampUsedQuotaData(this.entityName, 0, 0, this.instrumentGridToolBarOption.instrumentType,
                    this.currentQueryConfig.setForGroup ? this.currentQueryConfig.groupTailConfig : null,
                    this.currentQueryConfig.entityTailConfigs, null, this.currentQueryConfig.rolloverToCash)))
            .pipe(takeUntil(this.unsubscribe));

        const UsedSimuQuota$ = this.dataService.getAampUsedQuotaDataSimu(this.entityName, this.currentQueryConfig.modelId, 0, 0, this.instrumentGridToolBarOption.instrumentType,
            this.currentQueryConfig.setForGroup ? this.currentQueryConfig.groupTailConfig : null,
            this.currentQueryConfig.entityTailConfigs, null, this.currentQueryConfig.rolloverToCash)
            .pipe(takeUntil(this.unsubscribe));

        const entityDerivativeHoldingsSimu$ = this.dataService.getEntityDerivativeHoldingsSimu(this.entityName, this.currentQueryConfig.modelId, 0, 0, this.instrumentGridToolBarOption.instrumentType,
            this.currentQueryConfig.setForGroup ? this.currentQueryConfig.groupTailConfig : null,
            this.currentQueryConfig.entityTailConfigs, null, this.currentQueryConfig.rolloverToCash);

        combineLatest(derivativeHoldings$, UsedSimuQuota$, entityDerivativeHoldingsSimu$).pipe(takeUntil(this.unsubscribe))
            .subscribe(([myDerivativeHoldings, myUsedSimuQuota, myDerivativeHoldingsSimu]) => {
                this.usedQuotaData = myDerivativeHoldings.derivativeHoldings;
                if (this.store.value.user.isAdmin && _.any(this.usedQuotaData, (item: any) => item.maxQuota === 0)) {
                    this.addNewInstrumentProductsToQuotaArray(this.usedQuotaData);
                    this.openQuotaModal(true);
                }
                this.usedQuotaDataSimu = myUsedSimuQuota.simuProductNotional.sort((a, b) => a.productName.localeCompare(b.productName));
                if (this.store.value.user.isAdmin && _.any(this.usedQuotaDataSimu, (item: any) => item.maxQuota === 0)) {
                    this.addNewInstrumentProductsToQuotaArray(this.usedQuotaDataSimu);
                    this.openQuotaModal(true);
                }
                this.derivativeHoldings = myDerivativeHoldingsSimu.derivativeHoldings.sort((a, b) => a.productName.localeCompare(b.productName));
                for (const elt of this.usedQuotaDataSimu) {
                    this.highlightedSimu[elt.productName] = true;
                }
            }, error => alert('Error happened, while trying to load Used Quota Data" (+simu) ...'));
    }

    public addNewInstrumentProductsToQuotaArray(usedQuotaData: any[]) {
        _.each(usedQuotaData, (item: any) => {
            if (item.maxQuota === 0 && !_.any(this.aampQuotas, (x: any) => x.productName === item.productName)) {
                this.aampQuotas.push({
                    rowKey: null,
                    entityId: this.entityId,
                    maxQuota: 0,
                    productName: item.productName,
                    usedQuota: null
                });
            }
        });
    }

    public getCcrAndEceData() {
        this.showCounterPartyGridOverlay();

        this.dataService.getEntityCounterPartyCcrAndEce(this.entityName, null,
            this.currentQueryConfig.setForGroup ? this.currentQueryConfig.groupTailConfig : null,
            this.currentQueryConfig.entityTailConfigs, null, this.currentQueryConfig.rolloverToCash,
            this.currentQueryConfig.modelId)
            .pipe(takeUntil(this.unsubscribe))
            .subscribe(x => {
                this.entityCcrExeChartData = x.entityCounterPartyCcrAndEces;
                this.dataService.getLastMonthEntityCounterPartyCcrAndEce(this.entityName,
                    this.currentQueryConfig.setForGroup ? this.currentQueryConfig.groupTailConfig : null,
                    this.currentQueryConfig.entityTailConfigs, null, this.currentQueryConfig.rolloverToCash)
                    .pipe(takeUntil(this.unsubscribe))
                    .subscribe(lastMonthData => {
                        if (this.currentQueryConfig.modelId) {
                            this.dataService.getTradeInformation(this.currentQueryConfig.modelId).subscribe(res => {
                                const instruments: InstrumentItem[] = this.utilityService.simuRxCallback(res);
                                if (instruments && instruments.length > 0) {
                                    this.modelLeg1NotionalInUsd = this.getModelNotionalInUsd(instruments);
                                    this.prepareEntityCounterpartiesGrid(x.entityCounterPartyCcrAndEces, lastMonthData.entityCounterPartyCcrAndEces, this.modelLeg1NotionalInUsd);
                                    this.hideCounterPartyGridOverlay();
                                }
                            }, error => {
                                alert('Error happened while getting trade data.');
                            });
                        } else {
                            this.prepareEntityCounterpartiesGrid(x.entityCounterPartyCcrAndEces, lastMonthData.entityCounterPartyCcrAndEces, null);
                            this.hideCounterPartyGridOverlay();
                        }

                    }, error2 => {
                        this.counterPartyGridData = this.prepareDataForWccrGrid(x.entityCounterPartyCcrAndEces, [], null);
                    });
            }, error1 => {
                this.hideCounterPartyGridOverlay();
                this.entityCcrExeChartData = [];
                this.counterPartyGridData = [];
                alert('Error Happened while trying to load "Ccr and Ece Data"...');
            });
    }

    public getCcrAndEceDataSimu(callback: any) {
        this.showCounterPartyGridOverlay();
        const data$ = this.dataService.getEntityCounterPartyCcrAndEce(this.entityName, null,
            this.currentQueryConfig.setForGroup ? this.currentQueryConfig.groupTailConfig : null,
            this.currentQueryConfig.entityTailConfigs, null, this.currentQueryConfig.rolloverToCash,
            this.currentQueryConfig.modelId);
        const dataSimu$ = this.dataService.getEntityCounterPartyCcrAndEceSimu(this.entityName, null,
            this.currentQueryConfig.setForGroup ? this.currentQueryConfig.groupTailConfig : null,
            this.currentQueryConfig.entityTailConfigs, null, this.currentQueryConfig.rolloverToCash,
            this.currentQueryConfig.modelId);
        combineLatest(data$, dataSimu$)
            .pipe(takeUntil(this.unsubscribe))
            .subscribe(([data, dataSimu]) => {

                this.entityCcrExeChartData = data.entityCounterPartyCcrAndEces;
                this.entityCcrExeChartDataSimu = dataSimu.entityCounterPartyCcrAndEces;
                this.dataService.getLastMonthEntityCounterPartyCcrAndEce(this.entityName,
                    this.currentQueryConfig.setForGroup ? this.currentQueryConfig.groupTailConfig : null,
                    this.currentQueryConfig.entityTailConfigs, null, this.currentQueryConfig.rolloverToCash)
                    .pipe(takeUntil(this.unsubscribe))
                    .subscribe(lastMonthData => {
                        if (this.currentQueryConfig.modelId) {
                            this.dataService.getTradeInformation(this.currentQueryConfig.modelId).subscribe(result => {
                                if (result.tradeInstruments && result.tradeInstruments.length > 0) {
                                    const instruments: InstrumentItem[] = this.utilityService.simuRxCallback(result.tradeInstruments);
                                    this.modelLeg1NotionalInUsd = this.getModelNotionalInUsd(instruments);
                                    this.prepareEntityCounterpartiesGrid(dataSimu.entityCounterPartyCcrAndEces, lastMonthData.entityCounterPartyCcrAndEces, this.modelLeg1NotionalInUsd);
                                    this.hideCounterPartyGridOverlay();
                                }
                            }, error => {
                                alert('Error happened while getting trade data  (+simu).');
                                if (callback) {
                                    callback();
                                }
                            });
                        } else {
                            this.prepareEntityCounterpartiesGrid(dataSimu.entityCounterPartyCcrAndEces, lastMonthData.entityCounterPartyCcrAndEces, null);
                            this.hideCounterPartyGridOverlay();
                        }
                        if (callback) {
                            callback();
                        }
                    }, error2 => {
                        this.counterPartyGridData = this.prepareDataForWccrGrid(dataSimu.entityCounterPartyCcrAndEces, [], null);
                        if (callback) {
                            callback();
                        }
                    });
            }, error1 => {
                this.hideCounterPartyGridOverlay();
                this.entityCcrExeChartData = [];
                this.counterPartyGridData = [];
                alert('Error Happened while trying to load "Ccr and Ece Data"...');
                if (callback) {
                    callback();
                }
            });
    }
    public getModelNotionalInUsd(modelInstruments: any[]) {
        let totalLeg1Notional = 0;
        const knownIDs = [];
        modelInstruments.forEach(item => {
            let fxRateObj = this.getFxRate(item['leg1Currency']);
            if (knownIDs.indexOf(item.leg1Id) === -1 && !isNil(fxRateObj)) {
                totalLeg1Notional = totalLeg1Notional + (Math.abs(item['leg1Notional']) / Math.abs(fxRateObj['fxRate']));
                knownIDs.push(item.leg1Id);
            }
        });
        return this.utilityService.roundNumberTo2DecimalPlaces(totalLeg1Notional);
    }

    public getFxRate(fromCurrency: string): number {
        return _.find(this.fxRates, function (fxRate) {
            return fxRate.currency == fromCurrency;
        })
    }

    private prepareEntityCounterpartiesGrid(entityCcrExeChartData: any[], lastMonthEntityCcrExeChartData: any[], modelNotionalInUsd: any) {
        this.counterPartyGridData = this.prepareDataForWccrGrid(entityCcrExeChartData, lastMonthEntityCcrExeChartData, modelNotionalInUsd);
        this.form = this.fb.group({
            counterParties: this.fb.array(_.map(this.counterPartyGridData,
                counterParty => this.createCounterPartyFormControl(counterParty)))
        });

    }

    public createCounterPartyFormControl(obj: any) {
        return this.fb.group({
            counterParty: [obj.counterParty],
            ccr: [obj.ccr],
            timeStep: [obj.timeStep],
            timeStepDate: [obj.timeStepDate],
            w2Var: [obj.w2Var],
            ple: [obj.ple],
            occur: [obj.occur],
            treasurySf: [obj.treasurySf],
            treasurySfFormatted: [obj.treasurySfFormatted],
            cashSf: [obj.cashSf],
            cashSfFormatted: [obj.cashSfFormatted],
            lineChartData: [obj.lineChartData],
            lastMonthWccr: [obj.lastMonthWccr],
            lastMonthW2VarMUsd: [obj.lastMonthW2VarMUsd],
            lastMonthPleMUsd: [obj.lastMonthPleMUsd],
            zeroToTwoYearsWccr: [obj.zeroToTwoYearsWccr],
            zeroToTwoYearsW2VarMUsd: [obj.zeroToTwoYearsW2VarMUsd],
            zeroToTwoYearsPleMUsd: [obj.zeroToTwoYearsPleMUsd],
            zeroToTwoYearsDate: [obj.zeroToTwoYearsDate],
            twoToFiveYearsWccr: [obj.twoToFiveYearsWccr],
            twoToFiveYearsPleMUsd: [obj.twoToFiveYearsPleMUsd],
            twoToFiveYearsDate: [obj.twoToFiveYearsDate],
            fiveToTenYearsWccr: [obj.fiveToTenYearsWccr],
            fiveToTenYearsPleMUsd: [obj.fiveToTenYearsPleMUsd],
            fiveToTenYearsDate: [obj.fiveToTenYearsDate],
            notionalSharePercentage: [obj.notionalSharePercentage, Validators.pattern(/^[+-]?\d*\.?\d*$/)],
            notionalInUsd: [obj.notionalInUsd, Validators.pattern(/^[+-]?\d*\.?\d*$/)]
        });
    }

    public getInstruments(tooltipClickFilter?: boolean) {
        
        const from = tooltipClickFilter ? 0 : this.instrumentGridToolBarOption.horizon.from;
        const to = tooltipClickFilter ? 0 : this.instrumentGridToolBarOption.horizon.to;
        const productName = tooltipClickFilter && this.selectedTooltipProduct ? this.selectedTooltipProduct.productName : null;
    
        //this.showInstrumentGridOverlay();
        const instr$ = this.dataService.getEntityInstruments(this.entityName, from, to,
            this.instrumentGridToolBarOption.instrumentType,
            this.currentQueryConfig.setForGroup ? this.currentQueryConfig.groupTailConfig : null,
            this.currentQueryConfig.entityTailConfigs, null, this.currentQueryConfig.rolloverToCash, productName);
        if (this.currentQueryConfig.modelId === null) {
            instr$.pipe(takeUntil(this.unsubscribe))
                .subscribe(x => {

                    this.hideInstrumentGridOverlay();
                    this.instrumentGridData = x.instrumentGridItems;
                }, error => {
                    this.hideInstrumentGridOverlay();
                    this.instrumentGridData = [];
                    alert('Error Happened while trying to load "Instrument"...');
                });
        } else {
            const instrSimu$ = this.dataService.getEntityInstrumentsSimu(this.entityName, this.currentQueryConfig.modelId,
                from, to, this.instrumentGridToolBarOption.instrumentType,
                this.currentQueryConfig.setForGroup ? this.currentQueryConfig.groupTailConfig : null,
                this.currentQueryConfig.entityTailConfigs, null, this.currentQueryConfig.rolloverToCash, productName)
                .pipe(catchError(err => of([])));
            combineLatest(instr$, instrSimu$).pipe(takeUntil(this.unsubscribe))
                .subscribe(([item, itemSimu]) => {
                    this.hideInstrumentGridOverlay();
                    const instrumentGridDataSimu = itemSimu.instrumentGridItems;
                    // STUB
                    // const instrumentGridDataSimu = cloneDeep(item.instrumentGridItems);
                    for (const elt of instrumentGridDataSimu) {
                        elt['simu'] = true;
                    }
                    this.instrumentGridData = [...instrumentGridDataSimu, ...item.instrumentGridItems];
                }, error => {
                    this.hideInstrumentGridOverlay();
                    this.instrumentGridData = [];
                    alert('Error Happened while trying to load "Instrument"...');
                });
        }
    }

    public prepareDataForWccrGrid(entityCcrExeChartData: any[], lastMonthEntityCcrExeChartData: any[], modelNotionalInUsd: any): any[] {
        const counterPartyList = _.groupBy(entityCcrExeChartData, 'counterParty');
        const wccrGridData: any[] = [];
        for (const key of Object.keys(counterPartyList)) {
            let lineChartData = _.map(counterPartyList[key], x => +(x.ccr.toFixed(2)));
            if (this.constantsService.timesteps.length !== lineChartData.length) {
                lineChartData = this.fixMissingTimesteps(counterPartyList[key]);
            }

            const wccrBetweenZeroToTwo = this.getPeriodWccrData(0, 24, entityCcrExeChartData, key);
            const wccrBetweenTwoToFive = this.getPeriodWccrData(24, 60, entityCcrExeChartData, key);
            const wccrBetweenFiveToTen = this.getPeriodWccrData(60, 120, entityCcrExeChartData, key);
            const lastMonthWccr = this.getPeriodWccrData(0, 12, lastMonthEntityCcrExeChartData, key);
            wccrGridData.push({
                counterParty: key,
                lineChartData: lineChartData,
                lastMonthWccr: this.utilityService.commaGroupNumber(lastMonthWccr.wccr),
                lastMonthW2VarMUsd: this.utilityService.commaGroupNumber(lastMonthWccr.w2Var),
                lastMonthPleMUsd: this.utilityService.commaGroupNumber(lastMonthWccr.ple),
                zeroToTwoYearsWccr: this.utilityService.commaGroupNumber(wccrBetweenZeroToTwo.wccr),
                zeroToTwoYearsW2VarMUsd: this.utilityService.commaGroupNumber(wccrBetweenZeroToTwo.w2Var),
                zeroToTwoYearsPleMUsd: this.utilityService.commaGroupNumber(wccrBetweenZeroToTwo.ple),
                zeroToTwoYearsDate: wccrBetweenZeroToTwo.occur,

                twoToFiveYearsWccr: this.utilityService.commaGroupNumber(wccrBetweenTwoToFive.wccr),
                twoToFiveYearsPleMUsd: this.utilityService.commaGroupNumber(wccrBetweenTwoToFive.ple),
                twoToFiveYearsDate: wccrBetweenTwoToFive.occur,

                fiveToTenYearsWccr: this.utilityService.commaGroupNumber(wccrBetweenFiveToTen.wccr),
                fiveToTenYearsPleMUsd: this.utilityService.commaGroupNumber(wccrBetweenFiveToTen.ple),
                fiveToTenYearsDate: wccrBetweenFiveToTen.occur,

                notionalInUsd: modelNotionalInUsd != null ? modelNotionalInUsd : null,
                notionalSharePercentage: modelNotionalInUsd != null ? 100 : null,
            });
        }
        return wccrGridData;
    }

    public getPeriodWccrData(from: number, to: number, ccrExeData: any[], key: string): any {
        const periodData = _.filter(ccrExeData, x => from <= x.timeStep && to > x.timeStep && x.counterParty === key);
        if (periodData && periodData.length > 0) {
            const minWccrObj = _.min(periodData, x => x.ccr);
            const minPleObj = _.min(periodData, x => x.peakLiqExp);
            const minW2VarObj = _.min(periodData, x => x.timeStep);
            return {
                wccr: minWccrObj.ccr,
                w2Var: minW2VarObj.w2Var,
                //ple: minPleObj.peakLiqExp,
                ple: minWccrObj.peakLiqExp,
                occur: minWccrObj.occur
            };
        }
        return {
            wccr: 0,
            w2Var: 0,
            ple: 0,
            occur: 0
        };
    }


    public fixMissingTimesteps(data: any[]): any[] {
        return _.map(this.constantsService.timesteps, x => {
            if (_.any(data, item => item.timeStep === x)) {
                return +(_.find(data, y => y.timeStep === x).ccr.toFixed(2));
            }
            return 0;
        });
    }

    public showInstrumentGridOverlay() {
        if (this.instrumentGridApi) {
            this.instrumentGridApi.showLoadingOverlay();
        }
    }

    public hideInstrumentGridOverlay() {
        if (this.instrumentGridApi) {
            this.instrumentGridApi.hideOverlay();
        }
    }

    public showCounterPartyGridOverlay() {
        if (this.counterPartyGridApi) {
            this.counterPartyGridApi.showLoadingOverlay();
        }
    }

    public hideCounterPartyGridOverlay() {
        if (this.counterPartyGridApi) {
            this.counterPartyGridApi.hideOverlay();
        }
    }

    public subscribeOnPageReload() {
        this.appEvents.pageReload
            .pipe(takeUntil(this.unsubscribe))
            .subscribe(x => {
                this.isModellingOnPage = (this.store.value.urlModelToggle[this.router.url] &&
                    this.store.value.urlModelToggle[this.router.url] === true);
                this.isAnyModelSelectedForModelling = (this.store.value.modellingConfig.modelId != null);
                this.resetPageData();
                this.reloadPage();
            });
    }

    public onInstrumentExport() {
        this.instrumentGridApi.exportDataAsCsv(this.getGridCsvExportParam());
    }


    public getGridCsvExportParam(): CsvExportParams {
        const formatter = this.utilityService.csvFormatter;
        const param = <CsvExportParams>{};
        param.processCellCallback = function (columnParams) {
            let val: any;
            const column = columnParams.column.getColDef();
            if (column && column.field && column.field === 'legalEntity' && columnParams.value != null) {
                val = columnParams.value.dataName;
            } else {
                val = columnParams.value;
            }
            return formatter(val);
        };
        return param;
    }

    public getLegalEntityName(pageEntityId: string): string {
        const found = _.find(this.store.value.user.legalEntities, (x: any) => x.entityId === pageEntityId);
        if (found) {
            return found.dataName;
        }
        return null;
    }

    public reloadPageData() {
        this.appEvents.triggerPageReload();
    }

    ngOnDestroy() {
        this.unsubscribe.next();
        this.unsubscribe.complete();
    }

    isInvalidAndDirty(groupControl, columnName) {
        if (!groupControl || !columnName || !groupControl.controls[columnName]) {
            return true;
        }
        return !groupControl.controls[columnName].valid && groupControl.controls[columnName].dirty;
    }

    isInvalidAndTouched(groupControl, columnName) {
        if (!groupControl || !columnName || !groupControl.controls[columnName]) {
            return true;
        }
        return !groupControl.controls[columnName].valid && groupControl.controls[columnName].touched;
    }

    showCounterpartiesGridInputColumns() {
        return this.isModellingOnPage && this.isAnyModelSelectedForModelling;
    }

    showUpdateProjections() {
        return this.isModellingOnPage && this.isAnyModelSelectedForModelling && this.showUpdateProjectionsButton;
    }

    onCounterpartyGridNotionalSharePercentageInputChange(value, i, item) {
        let initialValue: any = this.modelLeg1NotionalInUsd;
        let calculatedValue: any = this.utilityService.roundNumberTo2DecimalPlaces((initialValue * value) / 100);
        item.controls.notionalInUsd.setValue(calculatedValue, { emitEvent: false });
        this.showUpdateProjectionsButton = (item.controls.notionalSharePercentage.dirty || item.controls.notionalInUsd.dirty)
            && item.controls.notionalSharePercentage.valid && item.controls.notionalInUsd.valid
            && !!(Number(item.controls.notionalSharePercentage.value) - 100);
    }

    onCounterpartyGridNotionalInUsdInputChange(value, i, item) {
        let initialValue: any = this.modelLeg1NotionalInUsd;
        let calculatedValue: any = this.utilityService.roundNumberTo2DecimalPlaces((value / initialValue) * 100);
        item.controls.notionalSharePercentage.setValue(calculatedValue, { emitEvent: false });
        this.showUpdateProjectionsButton = (item.controls.notionalSharePercentage.dirty || item.controls.notionalInUsd.dirty)
            && item.controls.notionalSharePercentage.valid && item.controls.notionalInUsd.valid
            && !!(Number(item.controls.notionalInUsd.value) - Number(this.modelLeg1NotionalInUsd));

    }

    onCounterpartyGridNotionalStatusChange(valid: boolean) {
        if (!valid) {
            this.showUpdateProjectionsButton = false;
        }
    }

    onCounterpartyGridNotionalBlur(isWrong, item) {
        if (isWrong) {
            this.resetCpyShareNotional(item);
        }
    }
    resetCpyShareNotional(item) {
        item.controls.notionalSharePercentage.setValue(100, { emitEvent: false });
        item.controls.notionalInUsd.setValue(this.modelLeg1NotionalInUsd, { emitEvent: false });
        item.controls.notionalSharePercentage.markAsPristine();
        item.controls.notionalInUsd.markAsPristine();
        this.showUpdateProjectionsButton = false;

    }

    updateProjections() {
        if (!this.projectionsAreBeingUpdated) {
            const message = 'Cannot update projections: ';
            const req: UpdateProjectionsRequest = { counterParties: [], shares: [] };
            for (const cpy of this.counterPartyList.controls) {
                req.counterParties.push(cpy.value.counterParty);
                req.shares.push(cpy.value.notionalSharePercentage + '');
            }
            this.dataService.updateProjections(this.currentQueryConfig.modelId, req).pipe(takeUntil(this.unsubscribe)).subscribe(
                success => {
                    if (success) {
                        this.getCcrAndEceDataSimu(() => {
                            this.projectionsAreBeingUpdated = false;
                            this.updateProjectionsLabel = 'UPDATE PROJECTIONS';
                            this.showUpdateProjectionsButton = false;
                            this.reloadPageData();
                        });
                    } else {
                        alert(message);
                        this.projectionsAreBeingUpdated = false;
                        this.showUpdateProjectionsButton = false;
                        this.updateProjectionsLabel = 'UPDATE PROJECTIONS';
                    }
                },
                error => {
                    alert(message + ": " + error);
                    this.projectionsAreBeingUpdated = false;
                    this.showUpdateProjectionsButton = false;
                    this.updateProjectionsLabel = 'UPDATE PROJECTIONS';
                }
            );
            this.updateProjectionsLabel = 'UPDATING PROJECTIONS...';
            this.projectionsAreBeingUpdated = true;
        }
    }
    gotoCounterParty(counterPartyName: string) {
        let url = '/counterParty/' + this.entityId + '/' + counterPartyName;
        this.router.navigateByUrl(url);
    }
}

